import { extractZipAndCityFromAddress } from '@plato/shared-util/event-address';
import { formatISOTimeForDisplay } from '@plato/shared-util/time-utils';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Card, CardContent, CardDetail, CardEnd, CardFront, CardTitle } from '@plato/ui/containers/card';
import { cn } from '@plato/ui/util/classes';
import { CheckIcon, MapPinIcon, RefreshCwIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetManyEventsListItem } from '../../../server/types/infer-trpc-types.ts';
import { getEventMetadata } from '../../../utils/event-utils.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { EventDetailsDialog } from './event-details-dialog.tsx';

export type Props = {
    className?: string;
    date: string;
    event: GetManyEventsListItem;
};

/** An event in the calendar: who, when and where. Opens the event dialog, which is also where a visit is logged. */
export function EventCard({ className, date, event }: Props): ReactNode {
    const { t } = useTranslation('events');
    const sendEvent = useAnalytics();

    const handleClick = (): void => {
        void DialogStateManager.show(EventDetailsDialog, {
            eventId: event.event_id,
            date,
            analyticsSource: 'calendar',
        });
        sendEvent('event_clicked', getEventMetadata(event));
    };

    const isLogged = event.notes.length > 0;
    const isRecurring = (event.recurrenceRule?.weeks ?? 0) > 0;
    const hasTime = !!event.start_time && !!event.end_time;
    // The customer names the visit; the title only stands in when there is none
    const name = event.customer?.customerName ?? event.title ?? t('events__card_default_title');
    // ZIP and city say where at a glance; the whole address is the fallback when it can't be read
    const zipAndCity = extractZipAndCityFromAddress(event.address);
    const place = zipAndCity?.city ? `${zipAndCity.zipCode} ${zipAndCity.city}` : event.address;

    return (
        <Card
            tint={isLogged ? 'success' : 'brand'}
            className={cn('cursor-grab shadow-xs', className)}
            onClick={handleClick}
        >
            <CardFront>
                <CardContent className="gap-tight">
                    <CardTitle className="truncate text-body text-content">{name}</CardTitle>
                    {(hasTime || isRecurring || place) && (
                        <CardDetail className="flex items-center gap-cozy text-caption text-content-weak">
                            <div className="flex shrink-0 items-center gap-1.5">
                                {event.start_time && event.end_time && (
                                    <span>
                                        {t('card_time_format', {
                                            startTime: formatISOTimeForDisplay(event.start_time),
                                            endTime: formatISOTimeForDisplay(event.end_time),
                                        })}
                                    </span>
                                )}
                                {isRecurring && <RefreshCwIcon className="size-3" />}
                            </div>
                            {place && (
                                <div className="ml-auto flex min-w-0 items-center gap-tight">
                                    <MapPinIcon className="size-3 shrink-0" />
                                    <span className="truncate">{place}</span>
                                </div>
                            )}
                        </CardDetail>
                    )}
                </CardContent>
                {isLogged && (
                    <CardEnd>
                        <CheckIcon className="size-3.5 stroke-[2.5] text-success" />
                    </CardEnd>
                )}
            </CardFront>
        </Card>
    );
}
